export const THEME_APPEARANCE_STORAGE_KEY = "bp-followzap:web-beta:appearance:v1";
export const THEME_APPEARANCE_STORAGE_KEY_V2 = "bp-followzap:web-beta:appearance:v2";
export const THEME_APPEARANCE_V2_SCOPE = "web-beta" as const;
export const THEME_APPEARANCE_COMMAND_EVENT = "bp-theme-appearance-command";

const LEGACY_COLOR_TOKENS = [
  "primary",
  "primary-hover",
  "secondary",
  "background",
  "surface",
  "sidebar",
  "header",
  "text",
  "text-muted",
  "border",
  "success",
  "warning",
  "danger",
  "info",
] as const;

export const SIDEBAR_COLOR_TOKENS = [
  "sidebar-bg",
  "sidebar-text",
  "sidebar-icon",
  "sidebar-hover-bg",
  "sidebar-active-bg",
  "sidebar-active-text",
  "sidebar-active-icon",
  "sidebar-border",
  "sidebar-divider",
  "sidebar-brand-bg",
  "sidebar-brand-text",
  "sidebar-badge-bg",
  "sidebar-badge-text",
] as const;

export const HEADER_COLOR_TOKENS = [
  "header-bg",
  "header-text",
  "header-text-muted",
  "header-icon",
  "header-border",
  "header-action-bg",
  "header-action-hover",
  "header-action-icon",
] as const;

export const FIELD_COLOR_TOKENS = [
  "field-bg",
  "field-text",
  "field-placeholder",
  "field-border",
  "field-border-hover",
  "field-border-focus",
  "field-focus-ring",
  "field-disabled-bg",
  "field-disabled-text",
  "field-readonly-bg",
  "field-error",
  "field-icon",
] as const;

export const CHOICE_COLOR_TOKENS = [
  "choice-bg",
  "choice-border",
  "choice-checked-bg",
  "choice-indicator",
  "choice-focus-ring",
  "choice-error",
] as const;

const BADGE_TYPE_COLOR_TOKENS = [
  "badge-type-paid-bg",
  "badge-type-paid-text",
  "badge-type-personalized-bg",
  "badge-type-personalized-text",
  "badge-type-cold-bg",
  "badge-type-cold-text",
  "badge-type-lost-bg",
  "badge-type-lost-text",
] as const;

const BADGE_COLOR_TOKENS = [
  "badge-neutral-bg",
  "badge-neutral-text",
  "badge-neutral-border",
  "badge-info-bg",
  "badge-info-text",
  "badge-info-border",
  "badge-success-bg",
  "badge-success-text",
  "badge-success-border",
  "badge-warning-bg",
  "badge-warning-text",
  "badge-warning-border",
  "badge-danger-bg",
  "badge-danger-text",
  "badge-danger-border",
] as const;

const ALERT_COLOR_TOKENS = [
  "alert-info-bg",
  "alert-info-text",
  "alert-info-border",
  "alert-success-bg",
  "alert-success-text",
  "alert-success-border",
  "alert-warning-bg",
  "alert-warning-text",
  "alert-warning-border",
  "alert-danger-bg",
  "alert-danger-text",
  "alert-danger-border",
] as const;

const LOADING_COLOR_TOKENS = [
  "loading-skeleton-bg",
  "loading-skeleton-highlight",
  "loading-spinner",
  "loading-text",
] as const;

const CARD_COLOR_TOKENS = [
  "card-parent-bg",
  "card-child-bg",
  "card-accent",
  "card-icon-bg",
  "card-value-color",
] as const;

const TABS_COLOR_TOKENS = [
  "tabs-bg",
  "tabs-text",
  "tabs-active-text",
  "tabs-hover",
  "tabs-border",
  "tabs-focus-ring",
] as const;
const FLOATING_COLOR_TOKENS = [
  "floating-bg",
  "floating-text",
  "floating-border",
  "floating-hover",
  "floating-focus-ring",
] as const;
const FLOATING_APPEARANCE_TOKENS = [...FLOATING_COLOR_TOKENS, "floating-shadow"] as const;
const OVERLAY_COLOR_TOKENS = [
  "overlay-surface-bg",
  "overlay-surface-text",
  "overlay-surface-border",
  "overlay-backdrop",
] as const;
const OVERLAY_APPEARANCE_TOKENS = [...OVERLAY_COLOR_TOKENS, "overlay-surface-shadow"] as const;

const COLOR_TOKENS = [...LEGACY_COLOR_TOKENS, ...SIDEBAR_COLOR_TOKENS, ...HEADER_COLOR_TOKENS, ...FIELD_COLOR_TOKENS, ...CHOICE_COLOR_TOKENS, ...BADGE_TYPE_COLOR_TOKENS, ...BADGE_COLOR_TOKENS, ...ALERT_COLOR_TOKENS, ...LOADING_COLOR_TOKENS, ...CARD_COLOR_TOKENS, ...TABS_COLOR_TOKENS, ...FLOATING_COLOR_TOKENS, ...OVERLAY_COLOR_TOKENS] as const;
const LEGACY_APPEARANCE_TOKENS = [...LEGACY_COLOR_TOKENS, "font-family", "font-size-base", "radius"] as const;

export const BUTTON_COLOR_TOKENS = [
  "button-primary-bg",
  "button-primary-hover",
  "button-primary-text",
  "button-secondary-bg",
  "button-secondary-hover",
  "button-secondary-text",
  "button-outline-bg",
  "button-outline-hover",
  "button-outline-text",
  "button-outline-border",
  "button-ghost-hover",
  "button-danger-bg",
  "button-danger-hover",
  "button-danger-text",
  "button-focus-ring",
] as const;

const BUTTON_APPEARANCE_TOKENS = [...BUTTON_COLOR_TOKENS, "button-disabled-opacity", "button-radius"] as const;
const FIELD_APPEARANCE_TOKENS = [...FIELD_COLOR_TOKENS, "field-radius"] as const;
const CHOICE_APPEARANCE_TOKENS = [...CHOICE_COLOR_TOKENS, "choice-disabled-opacity"] as const;
const APPEARANCE_TOKENS = [
  ...LEGACY_APPEARANCE_TOKENS,
  ...SIDEBAR_COLOR_TOKENS,
  ...HEADER_COLOR_TOKENS,
  ...BUTTON_APPEARANCE_TOKENS,
  ...FIELD_APPEARANCE_TOKENS,
  ...CHOICE_APPEARANCE_TOKENS,
  ...BADGE_TYPE_COLOR_TOKENS,
  ...BADGE_COLOR_TOKENS,
  ...ALERT_COLOR_TOKENS,
  ...LOADING_COLOR_TOKENS,
  ...CARD_COLOR_TOKENS,
  ...TABS_COLOR_TOKENS,
  ...FLOATING_APPEARANCE_TOKENS,
  ...OVERLAY_APPEARANCE_TOKENS,
] as const;

export type ThemeAppearanceToken = (typeof APPEARANCE_TOKENS)[number];
export type ThemeAppearanceTokens = Record<ThemeAppearanceToken, string>;
export type ThemeAppearanceOverrides = Partial<Record<ThemeAppearanceToken, string>>;

export const THEME_APPEARANCE_CSS_VARIABLES: Record<ThemeAppearanceToken, string> = {
  primary: "--fz-color-primary",
  "primary-hover": "--fz-color-primary-hover",
  secondary: "--fz-color-secondary",
  background: "--fz-color-background",
  surface: "--fz-color-surface",
  sidebar: "--fz-color-sidebar",
  "sidebar-bg": "--fz-sidebar-bg",
  "sidebar-text": "--fz-sidebar-text",
  "sidebar-icon": "--fz-sidebar-icon",
  "sidebar-hover-bg": "--fz-sidebar-hover-bg",
  "sidebar-active-bg": "--fz-sidebar-active-bg",
  "sidebar-active-text": "--fz-sidebar-active-text",
  "sidebar-active-icon": "--fz-sidebar-active-icon",
  "sidebar-border": "--fz-sidebar-border",
  "sidebar-divider": "--fz-sidebar-divider",
  "sidebar-brand-bg": "--fz-sidebar-brand-bg",
  "sidebar-brand-text": "--fz-sidebar-brand-text",
  "sidebar-badge-bg": "--fz-sidebar-badge-bg",
  "sidebar-badge-text": "--fz-sidebar-badge-text",
  "header-bg": "--fz-header-bg",
  "header-text": "--fz-header-text",
  "header-text-muted": "--fz-header-text-muted",
  "header-icon": "--fz-header-icon",
  "header-border": "--fz-header-border",
  "header-action-bg": "--fz-header-action-bg",
  "header-action-hover": "--fz-header-action-hover",
  "header-action-icon": "--fz-header-action-icon",
  header: "--fz-color-header",
  text: "--fz-color-text",
  "text-muted": "--fz-color-text-muted",
  border: "--fz-color-border",
  success: "--fz-color-success",
  warning: "--fz-color-warning",
  danger: "--fz-color-danger",
  info: "--fz-color-info",
  "font-family": "--fz-font-family",
  "font-size-base": "--fz-font-size-base",
  radius: "--fz-radius",
  "button-primary-bg": "--fz-button-primary-bg",
  "button-primary-hover": "--fz-button-primary-hover",
  "button-primary-text": "--fz-button-primary-text",
  "button-secondary-bg": "--fz-button-secondary-bg",
  "button-secondary-hover": "--fz-button-secondary-hover",
  "button-secondary-text": "--fz-button-secondary-text",
  "button-outline-bg": "--fz-button-outline-bg",
  "button-outline-hover": "--fz-button-outline-hover",
  "button-outline-text": "--fz-button-outline-text",
  "button-outline-border": "--fz-button-outline-border",
  "button-ghost-hover": "--fz-button-ghost-hover",
  "button-danger-bg": "--fz-button-danger-bg",
  "button-danger-hover": "--fz-button-danger-hover",
  "button-danger-text": "--fz-button-danger-text",
  "button-focus-ring": "--fz-button-focus-ring",
  "button-disabled-opacity": "--fz-button-disabled-opacity",
  "button-radius": "--fz-button-radius",
  "field-bg": "--fz-field-bg",
  "field-text": "--fz-field-text",
  "field-placeholder": "--fz-field-placeholder",
  "field-border": "--fz-field-border",
  "field-border-hover": "--fz-field-border-hover",
  "field-border-focus": "--fz-field-border-focus",
  "field-focus-ring": "--fz-field-focus-ring",
  "field-disabled-bg": "--fz-field-disabled-bg",
  "field-disabled-text": "--fz-field-disabled-text",
  "field-readonly-bg": "--fz-field-readonly-bg",
  "field-error": "--fz-field-error",
  "field-icon": "--fz-field-icon",
  "field-radius": "--fz-field-radius",
  "choice-bg": "--fz-choice-bg",
  "choice-border": "--fz-choice-border",
  "choice-checked-bg": "--fz-choice-checked-bg",
  "choice-indicator": "--fz-choice-indicator",
  "choice-focus-ring": "--fz-choice-focus-ring",
  "choice-error": "--fz-choice-error",
  "choice-disabled-opacity": "--fz-choice-disabled-opacity",
  "badge-type-paid-bg": "--fz-badge-type-paid-bg",
  "badge-type-paid-text": "--fz-badge-type-paid-text",
  "badge-type-personalized-bg": "--fz-badge-type-personalized-bg",
  "badge-type-personalized-text": "--fz-badge-type-personalized-text",
  "badge-type-cold-bg": "--fz-badge-type-cold-bg",
  "badge-type-cold-text": "--fz-badge-type-cold-text",
  "badge-type-lost-bg": "--fz-badge-type-lost-bg",
  "badge-type-lost-text": "--fz-badge-type-lost-text",
  "badge-neutral-bg": "--fz-badge-neutral-bg",
  "badge-neutral-text": "--fz-badge-neutral-text",
  "badge-neutral-border": "--fz-badge-neutral-border",
  "badge-info-bg": "--fz-badge-info-bg",
  "badge-info-text": "--fz-badge-info-text",
  "badge-info-border": "--fz-badge-info-border",
  "badge-success-bg": "--fz-badge-success-bg",
  "badge-success-text": "--fz-badge-success-text",
  "badge-success-border": "--fz-badge-success-border",
  "badge-warning-bg": "--fz-badge-warning-bg",
  "badge-warning-text": "--fz-badge-warning-text",
  "badge-warning-border": "--fz-badge-warning-border",
  "badge-danger-bg": "--fz-badge-danger-bg",
  "badge-danger-text": "--fz-badge-danger-text",
  "badge-danger-border": "--fz-badge-danger-border",
  "alert-info-bg": "--fz-alert-info-bg",
  "alert-info-text": "--fz-alert-info-text",
  "alert-info-border": "--fz-alert-info-border",
  "alert-success-bg": "--fz-alert-success-bg",
  "alert-success-text": "--fz-alert-success-text",
  "alert-success-border": "--fz-alert-success-border",
  "alert-warning-bg": "--fz-alert-warning-bg",
  "alert-warning-text": "--fz-alert-warning-text",
  "alert-warning-border": "--fz-alert-warning-border",
  "alert-danger-bg": "--fz-alert-danger-bg",
  "alert-danger-text": "--fz-alert-danger-text",
  "alert-danger-border": "--fz-alert-danger-border",
  "loading-skeleton-bg": "--fz-loading-skeleton-bg",
  "loading-skeleton-highlight": "--fz-loading-skeleton-highlight",
  "loading-spinner": "--fz-loading-spinner",
  "loading-text": "--fz-loading-text",
  "card-parent-bg": "--fz-card-parent-bg",
  "card-child-bg": "--fz-card-child-bg",
  "card-accent": "--fz-card-accent",
  "card-icon-bg": "--fz-card-icon-bg",
  "card-value-color": "--fz-card-value-color",
  "tabs-bg": "--fz-tabs-bg",
  "tabs-text": "--fz-tabs-text",
  "tabs-active-text": "--fz-tabs-active-text",
  "tabs-hover": "--fz-tabs-hover",
  "tabs-border": "--fz-tabs-border",
  "tabs-focus-ring": "--fz-tabs-focus-ring",
  "floating-bg": "--fz-floating-bg",
  "floating-text": "--fz-floating-text",
  "floating-border": "--fz-floating-border",
  "floating-hover": "--fz-floating-hover",
  "floating-focus-ring": "--fz-floating-focus-ring",
  "floating-shadow": "--fz-floating-shadow",
  "overlay-surface-bg": "--fz-overlay-surface-bg",
  "overlay-surface-text": "--fz-overlay-surface-text",
  "overlay-surface-border": "--fz-overlay-surface-border",
  "overlay-backdrop": "--fz-overlay-backdrop",
  "overlay-surface-shadow": "--fz-overlay-surface-shadow",
};

export const THEME_APPEARANCE_LEGACY_VARIABLES: Partial<Record<ThemeAppearanceToken, string[]>> = {
  primary: ["--brand"],
  "primary-hover": ["--brand-dark"],
  secondary: ["--icon-neutral"],
  background: ["--surface"],
  surface: ["--card", "--surface-card-2"],
  sidebar: ["--surface-sidebar"],
  header: ["--surface-header"],
  text: ["--navy", "--text-primary"],
  "text-muted": ["--muted", "--text-secondary", "--text-soft"],
  border: ["--border"],
};

export type ThemeAppearanceV2 = {
  version: 2;
  scope: typeof THEME_APPEARANCE_V2_SCOPE;
  overrides: ThemeAppearanceOverrides;
  savedAt?: string;
};

export type ThemeAppearanceV2ReadResult =
  | { status: "absent" }
  | { status: "valid"; appearance: ThemeAppearanceV2 }
  | { status: "invalid" };

export type ThemeAppearanceOverridesReadResult = {
  source: "none" | "v2" | "v2-invalid" | "v1-migrated" | "v1-invalid";
  overrides: ThemeAppearanceOverrides;
};

export type ThemeAppearance = {
  version: 1;
  tokens: ThemeAppearanceTokens;
};

export type ThemeAppearanceInput = {
  version?: unknown;
  tokens?: unknown;
} & Partial<Record<ThemeAppearanceToken, unknown>>;

export type ThemeAppearanceCommand =
  | { action: "preview"; overrides: ThemeAppearanceOverrides }
  | { action: "save"; overrides: ThemeAppearanceOverrides }
  | { action: "cancel" }
  | { action: "restore" }
  | { action: "sync" };

export const DEFAULT_THEME_APPEARANCE: ThemeAppearance = {
  version: 1,
  tokens: {
    primary: "#2563EB",
    "primary-hover": "#1d4ed8",
    secondary: "#64748B",
    background: "#f8f8f8",
    surface: "#F9FAFD",
    sidebar: "#FBFAF7",
    "sidebar-bg": "#071833",
    "sidebar-text": "#B8C6E6",
    "sidebar-icon": "#B8C6E6",
    "sidebar-hover-bg": "#0D244F",
    "sidebar-active-bg": "#163A86",
    "sidebar-active-text": "#FFFFFF",
    "sidebar-active-icon": "#FFFFFF",
    "sidebar-border": "#1C3768",
    "sidebar-divider": "#1C3768",
    "sidebar-brand-bg": "#0D244F",
    "sidebar-brand-text": "#FFFFFF",
    "sidebar-badge-bg": "#EF4444",
    "sidebar-badge-text": "#FFFFFF",
    "header-bg": "#F9FAFD",
    "header-text": "#0F1B3D",
    "header-text-muted": "#6B7280",
    "header-icon": "#6B7280",
    "header-border": "#DCE3F1",
    "header-action-bg": "transparent",
    "header-action-hover": "transparent",
    "header-action-icon": "#6B7280",
    header: "#FFFDFC",
    text: "#0F1B3D",
    "text-muted": "#6B7280",
    border: "#DCE3F1",
    success: "#16A34A",
    warning: "#D97706",
    danger: "#DC2626",
    info: "#2563EB",
    "font-family": '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", system-ui, sans-serif',
    "font-size-base": "1rem",
    radius: "0.75rem",
    "button-primary-bg": "#2563EB",
    "button-primary-hover": "#1d4ed8",
    "button-primary-text": "#FFFFFF",
    "button-secondary-bg": "#F8FAFC",
    "button-secondary-hover": "#F1F5F9",
    "button-secondary-text": "#0F1B3D",
    "button-outline-bg": "#FFFFFF",
    "button-outline-hover": "#F8FAFC",
    "button-outline-text": "#0F1B3D",
    "button-outline-border": "#DCE3F1",
    "button-ghost-hover": "#F1F5F9",
    "button-danger-bg": "#DC2626",
    "button-danger-hover": "#B91C1C",
    "button-danger-text": "#FFFFFF",
    "button-focus-ring": "#2563EB",
    "button-disabled-opacity": "0.5",
    "button-radius": "0.75rem",
    "field-bg": "#FFFFFF",
    "field-text": "#0F1B3D",
    "field-placeholder": "#6B7280",
    "field-border": "#DCE3F1",
    "field-border-hover": "#94A3B8",
    "field-border-focus": "#2563EB",
    "field-focus-ring": "rgba(37, 99, 235, 0.16)",
    "field-disabled-bg": "#F8FAFC",
    "field-disabled-text": "#6B7280",
    "field-readonly-bg": "#F8FAFC",
    "field-error": "#DC2626",
    "field-icon": "#6B7280",
    "field-radius": "0.75rem",
    "choice-bg": "#FFFFFF",
    "choice-border": "#DCE3F1",
    "choice-checked-bg": "#2563EB",
    "choice-indicator": "#FFFFFF",
    "choice-focus-ring": "rgba(37, 99, 235, 0.16)",
    "choice-error": "#DC2626",
    "choice-disabled-opacity": "0.5",
    "badge-type-paid-bg": "#D1FAE5",
    "badge-type-paid-text": "#047857",
    "badge-type-personalized-bg": "#FEF3C7",
    "badge-type-personalized-text": "#B45309",
    "badge-type-cold-bg": "#DBEAFE",
    "badge-type-cold-text": "#1D4ED8",
    "badge-type-lost-bg": "#FEE2E2",
    "badge-type-lost-text": "#DC2626",
    "badge-neutral-bg": "#F1F5F9",
    "badge-neutral-text": "#475569",
    "badge-neutral-border": "#CBD5E1",
    "badge-info-bg": "#DBEAFE",
    "badge-info-text": "#1D4ED8",
    "badge-info-border": "#BFDBFE",
    "badge-success-bg": "#DCFCE7",
    "badge-success-text": "#166534",
    "badge-success-border": "#BBF7D0",
    "badge-warning-bg": "#FEF3C7",
    "badge-warning-text": "#B45309",
    "badge-warning-border": "#FDE68A",
    "badge-danger-bg": "#FEE2E2",
    "badge-danger-text": "#B91C1C",
    "badge-danger-border": "#FECACA",
    "alert-info-bg": "#EFF6FF",
    "alert-info-text": "#1D4ED8",
    "alert-info-border": "#BFDBFE",
    "alert-success-bg": "#F0FDF4",
    "alert-success-text": "#15803D",
    "alert-success-border": "#BBF7D0",
    "alert-warning-bg": "#FFFBEB",
    "alert-warning-text": "#B45309",
    "alert-warning-border": "#FDE68A",
    "alert-danger-bg": "#FEF2F2",
    "alert-danger-text": "#B91C1C",
    "alert-danger-border": "#FECACA",
    "loading-skeleton-bg": "#E2E8F0",
    "loading-skeleton-highlight": "#F8FAFC",
    "loading-spinner": "#2563EB",
    "loading-text": "#6B7280",
    "card-parent-bg": "#F9FAFD",
    "card-child-bg": "#F9FAFD",
    "card-accent": "#2563EB",
    "card-icon-bg": "#EAF0FF",
    "card-value-color": "#0F1B3D",
    "tabs-bg": "transparent",
    "tabs-text": "#6B7280",
    "tabs-active-text": "#2563EB",
    "tabs-hover": "#1D4ED8",
    "tabs-border": "#DCE3F1",
    "tabs-focus-ring": "rgba(37, 99, 235, 0.16)",
    "floating-bg": "#FFFFFF",
    "floating-text": "#0F1B3D",
    "floating-border": "#DCE3F1",
    "floating-hover": "#F8FAFC",
    "floating-focus-ring": "rgba(37, 99, 235, 0.16)",
    "floating-shadow": "0 18px 40px rgba(15, 23, 42, 0.14)",
    "overlay-surface-bg": "#FFFFFF",
    "overlay-surface-text": "#0F1B3D",
    "overlay-surface-border": "#DCE3F1",
    "overlay-backdrop": "rgba(15, 23, 42, 0.45)",
    "overlay-surface-shadow": "0 18px 40px rgba(15, 23, 42, 0.14)",
  },
};

const COLOR_VALUE = /^(?:#[0-9a-f]{3,8}|(?:rgb|hsl)a?\([^(){};]{1,160}\)|[a-z][a-z0-9-]{0,39})$/i;
const FONT_FAMILY_VALUE = /^[a-z0-9\s,.'"-]{1,240}$/i;
const DIMENSION_VALUE = /^(?:0|(?:\d+(?:\.\d+)?)(?:px|rem|em|%))$/i;
const OPACITY_VALUE = /^(?:0|1|0?\.\d{1,3})$/;
const SHADOW_VALUE = /^(?:none|(?:(?:-?(?:0|\d+(?:\.\d+)?)(?:px|rem|em)?\s+){2,4})(?:rgba?\([^(){};]{1,160}\)|#[0-9a-f]{3,8}|[a-z][a-z0-9-]{0,39}))$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function tokenValido(token: ThemeAppearanceToken, value: unknown): value is string {
  if (typeof value !== "string") return false;
  const normalizado = value.trim();
  if (!normalizado || normalizado.length > 240) return false;
  if (COLOR_TOKENS.includes(token as (typeof COLOR_TOKENS)[number]) || BUTTON_COLOR_TOKENS.includes(token as (typeof BUTTON_COLOR_TOKENS)[number])) {
    return COLOR_VALUE.test(normalizado);
  }
  if (token === "font-family") return FONT_FAMILY_VALUE.test(normalizado);
  if (token === "button-disabled-opacity" || token === "choice-disabled-opacity") return OPACITY_VALUE.test(normalizado);
  if (token === "floating-shadow" || token === "overlay-surface-shadow") return SHADOW_VALUE.test(normalizado);
  return DIMENSION_VALUE.test(normalizado);
}

function storageDisponivel(storage?: Storage) {
  return typeof window !== "undefined" || Boolean(storage);
}

function obterStorage(storage?: Storage): Storage | null {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function tokenReconhecido(token: string): token is ThemeAppearanceToken {
  return APPEARANCE_TOKENS.includes(token as ThemeAppearanceToken);
}

function savedAtValido(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 100;
}

/**
 * Normaliza somente as chaves presentes. Ausência nunca recebe default.
 * Chaves desconhecidas ou valores inválidos tornam o conjunto inválido.
 */
export function normalizarThemeAppearanceOverrides(input: unknown): ThemeAppearanceOverrides | null {
  if (!isRecord(input)) return null;

  const overrides: ThemeAppearanceOverrides = {};
  for (const [token, value] of Object.entries(input)) {
    if (!tokenReconhecido(token) || !tokenValido(token, value)) return null;
    overrides[token] = value.trim();
  }
  return overrides;
}

export function normalizarThemeAppearanceV2(input: unknown): ThemeAppearanceV2 | null {
  if (!isRecord(input) || input.version !== 2 || input.scope !== THEME_APPEARANCE_V2_SCOPE) return null;
  if (input.savedAt !== undefined && !savedAtValido(input.savedAt)) return null;

  const overrides = normalizarThemeAppearanceOverrides(input.overrides);
  if (!overrides) return null;

  const normalizado: ThemeAppearanceV2 = {
    version: 2,
    scope: THEME_APPEARANCE_V2_SCOPE,
    overrides,
  };
  if (input.savedAt !== undefined) normalizado.savedAt = input.savedAt;
  return normalizado;
}

export function serializarThemeAppearanceV2(input: unknown, savedAt = new Date().toISOString()): string | null {
  const appearance = criarThemeAppearanceV2(input, savedAt);
  return appearance ? JSON.stringify(appearance) : null;
}

export function mesclarThemeAppearanceOverrides(
  base: ThemeAppearanceOverrides,
  ...fontes: ThemeAppearanceOverrides[]
): ThemeAppearanceOverrides {
  const mesclado: ThemeAppearanceOverrides = { ...base };
  for (const fonte of fontes) {
    for (const [token, value] of Object.entries(fonte)) {
      if (tokenReconhecido(token) && value !== undefined) mesclado[token] = value;
    }
  }
  return mesclado;
}

export function comporThemeAppearanceValores(
  efetivos: ThemeAppearanceTokens,
  savedOverrides: ThemeAppearanceOverrides,
  previewOverrides: ThemeAppearanceOverrides,
): ThemeAppearanceTokens {
  return {
    ...efetivos,
    ...savedOverrides,
    ...previewOverrides,
  };
}

export function removerThemeAppearanceOverride(
  overrides: ThemeAppearanceOverrides,
  token: ThemeAppearanceToken,
): ThemeAppearanceOverrides {
  const proximo = { ...overrides };
  delete proximo[token];
  return proximo;
}

export function themeAppearanceV2EstaVazio(appearance: ThemeAppearanceV2 | null | undefined) {
  return Boolean(appearance && Object.keys(appearance.overrides).length === 0);
}

export function criarThemeAppearanceV2(input: unknown, savedAt = new Date().toISOString()): ThemeAppearanceV2 | null {
  const overrides = normalizarThemeAppearanceOverrides(input);
  if (!overrides || !savedAtValido(savedAt)) return null;
  return {
    version: 2,
    scope: THEME_APPEARANCE_V2_SCOPE,
    overrides,
    savedAt,
  };
}

export function lerThemeAppearanceV2(storage?: Storage): ThemeAppearanceV2ReadResult {
  if (!storageDisponivel(storage)) return { status: "absent" };
  const fonte = obterStorage(storage);
  if (!fonte) return { status: "absent" };

  let bruto: string | null;
  try {
    bruto = fonte.getItem(THEME_APPEARANCE_STORAGE_KEY_V2);
  } catch {
    return { status: "invalid" };
  }
  if (bruto === null) return { status: "absent" };

  try {
    const appearance = normalizarThemeAppearanceV2(JSON.parse(bruto));
    return appearance ? { status: "valid", appearance } : { status: "invalid" };
  } catch {
    return { status: "invalid" };
  }
}

export function salvarThemeAppearanceV2(input: unknown, storage?: Storage, savedAt = new Date().toISOString()): ThemeAppearanceV2 | null {
  if (!storageDisponivel(storage)) return null;
  const fonte = obterStorage(storage);
  const appearance = criarThemeAppearanceV2(input, savedAt);
  if (!fonte || !appearance) return null;

  try {
    fonte.setItem(THEME_APPEARANCE_STORAGE_KEY_V2, JSON.stringify(appearance));
    return appearance;
  } catch {
    return null;
  }
}

function lerV1Bruto(storage?: Storage): { status: "absent" | "present"; value?: unknown } {
  if (!storageDisponivel(storage)) return { status: "absent" };
  const fonte = obterStorage(storage);
  if (!fonte) return { status: "absent" };

  try {
    const bruto = fonte.getItem(THEME_APPEARANCE_STORAGE_KEY);
    if (bruto === null) return { status: "absent" };
    return { status: "present", value: JSON.parse(bruto) };
  } catch {
    return { status: "present" };
  }
}

/**
 * O v1 era completo e não registra dirty tokens. Valores iguais ao default
 * histórico são omitidos para evitar reintroduzir defaults contaminados.
 */
export function migrarThemeAppearanceV1ParaV2(input: unknown, savedAt = new Date().toISOString()): ThemeAppearanceV2 | null {
  if (!isRecord(input) || input.version !== 1 || !isRecord(input.tokens)) return null;

  const overrides: ThemeAppearanceOverrides = {};
  for (const [token, value] of Object.entries(input.tokens)) {
    if (!tokenReconhecido(token) || !tokenValido(token, value)) continue;
    const normalizado = value.trim();
    if (normalizado === DEFAULT_THEME_APPEARANCE.tokens[token]) continue;
    overrides[token] = normalizado;
  }
  return criarThemeAppearanceV2(overrides, savedAt);
}

export function lerThemeAppearanceOverrides(storage?: Storage): ThemeAppearanceOverridesReadResult {
  const v2 = lerThemeAppearanceV2(storage);
  if (v2.status === "valid") return { source: "v2", overrides: v2.appearance.overrides };
  if (v2.status === "invalid") return { source: "v2-invalid", overrides: {} };

  const v1 = lerV1Bruto(storage);
  if (v1.status === "absent") return { source: "none", overrides: {} };

  const migrado = migrarThemeAppearanceV1ParaV2(v1.value);
  const salvo = salvarThemeAppearanceV2(migrado?.overrides ?? {}, storage);
  return {
    source: migrado && salvo ? "v1-migrated" : "v1-invalid",
    overrides: salvo?.overrides ?? {},
  };
}

function tokensDaEntrada(input: ThemeAppearanceInput): Record<string, unknown> {
  return isRecord(input.tokens) ? input.tokens : (input as Record<string, unknown>);
}

export function normalizarThemeAppearance(input: unknown, base: ThemeAppearance = DEFAULT_THEME_APPEARANCE): ThemeAppearance | null {
  if (!isRecord(input) || (input.version !== undefined && input.version !== 1)) return null;

  const entrada = tokensDaEntrada(input as ThemeAppearanceInput);
  const tokens = { ...base.tokens };
  for (const token of APPEARANCE_TOKENS) {
    if (!(token in entrada)) continue;
    const valor = entrada[token];
    if (!tokenValido(token, valor)) return null;
    tokens[token] = valor.trim();
  }
  return { version: 1, tokens };
}

function estruturaPersistidaValida(value: unknown): value is { version: 1; tokens: Record<string, unknown> } {
  if (!isRecord(value) || value.version !== 1 || !isRecord(value.tokens)) return false;
  const tokens = value.tokens;
  return LEGACY_APPEARANCE_TOKENS.every((token) => token in tokens);
}

function removerAparenciaInvalida(storage: Storage) {
  try {
    storage.removeItem(THEME_APPEARANCE_STORAGE_KEY);
  } catch {}
}

export function lerThemeAppearanceSalva(storage?: Storage): ThemeAppearance | null {
  if (typeof window === "undefined" && !storage) return null;

  const fonte = storage ?? window.localStorage;
  let bruto: string | null;
  try {
    bruto = fonte.getItem(THEME_APPEARANCE_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!bruto) return null;

  let salvo: unknown;
  try {
    salvo = JSON.parse(bruto);
  } catch {
    removerAparenciaInvalida(fonte);
    return null;
  }

  if (!estruturaPersistidaValida(salvo)) {
    removerAparenciaInvalida(fonte);
    return null;
  }

  const normalizado = normalizarThemeAppearance(salvo);
  if (normalizado) return normalizado;
  removerAparenciaInvalida(fonte);
  return null;
}

export function salvarThemeAppearance(input: unknown, storage?: Storage): ThemeAppearance | null {
  if (typeof window === "undefined" && !storage) return null;

  try {
    const fonte = storage ?? window.localStorage;
    const base = lerThemeAppearanceSalva(fonte) ?? DEFAULT_THEME_APPEARANCE;
    const normalizado = normalizarThemeAppearance(input, base);
    if (!normalizado) return null;
    fonte.setItem(THEME_APPEARANCE_STORAGE_KEY, JSON.stringify(normalizado));
    return normalizado;
  } catch {
    return null;
  }
}

export function restaurarThemeAppearance(storage?: Storage) {
  if (typeof window === "undefined" && !storage) return;
  try {
    (storage ?? window.localStorage).removeItem(THEME_APPEARANCE_STORAGE_KEY);
  } catch {}
}

function emitirComando(command: ThemeAppearanceCommand) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<ThemeAppearanceCommand>(THEME_APPEARANCE_COMMAND_EVENT, { detail: command }));
}

export function solicitarPreviewThemeAppearance(overrides: ThemeAppearanceOverrides) {
  emitirComando({ action: "preview", overrides });
}

export function solicitarSalvarThemeAppearance(overrides: ThemeAppearanceOverrides) {
  emitirComando({ action: "save", overrides });
}

export function solicitarCancelarThemeAppearance() {
  emitirComando({ action: "cancel" });
}

export function solicitarRestaurarThemeAppearance() {
  emitirComando({ action: "restore" });
}

export function solicitarSincronizarThemeAppearance() {
  emitirComando({ action: "sync" });
}
